import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAuth } from '../../AuthContext';
import configData from '../../configure';

export default function ClassSectionList({ orgId }) {
  const { axios } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  // 1. Fetch Classes of the organization
  const { data: rawClassesList, isLoading, error, refetch } = useQuery({
    queryKey: ['classesForSectionList', orgId],
    queryFn: async () => {
      const response = await axios.get(`${configData.GET_CLASSES_BY_ORG}/${orgId}`);
      return response.data || [];
    },
    enabled: !!orgId,
    staleTime: 2 * 60 * 1000,
  });

  // 2. Group classes by their sectionName (KV Center)
  const groupedData = useMemo(() => {
    if (!rawClassesList) return { groups: {}, totalCenters: 0, totalClasses: 0 };

    const groups = {};
    let totalClasses = 0;

    rawClassesList.forEach(cls => {
      const section = (cls.sectionName || 'General').trim();
      if (!groups[section]) {
        groups[section] = [];
      }
      groups[section].push({
        classId: cls.classId || cls.id,
        className: cls.className || 'Unknown Class',
        original: cls
      });
      totalClasses++;
    });

    Object.keys(groups).forEach(sec => {
      groups[sec].sort((a, b) => a.className.localeCompare(b.className));
    });

    const totalCenters = Object.keys(groups).length;

    return { groups, totalCenters, totalClasses };
  }, [rawClassesList]);

  // 3. Filter groups based on search term
  const filteredGroups = useMemo(() => {
    const { groups } = groupedData;
    const query = searchTerm.trim().toLowerCase();

    if (!query) return groups;

    const filtered = {};
    Object.entries(groups).forEach(([sectionName, classesList]) => {
      const sectionMatches = sectionName.toLowerCase().includes(query);
      const matchingClasses = classesList.filter(c => c.className.toLowerCase().includes(query));

      if (sectionMatches) {
        filtered[sectionName] = classesList;
      } else if (matchingClasses.length > 0) {
        filtered[sectionName] = matchingClasses;
      }
    });

    return filtered;
  }, [groupedData, searchTerm]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 2 }}>
        <CircularProgress size={40} sx={{ color: '#0070d2' }} />
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          Loading classes hierarchy...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, m: 3, bgcolor: '#fff5f5', color: '#e53e3e', borderRadius: '8px', border: '1px solid #feb2b2' }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>Error Loading Class Sections</Typography>
        <Typography variant="body2">{error.message || 'Please check your connection and try again.'}</Typography>
      </Box>
    );
  }

  const { totalCenters, totalClasses } = groupedData;
  const filteredCentersCount = Object.keys(filteredGroups).length;

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      
      {/* Title & Search bar Row */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, borderBottom: '1px solid #e2e8f0', pb: 2.5 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>
            KV Centers & Associated Classes
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
            Overview of learning centers and active class sections.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, width: { xs: '100%', sm: 'auto' }, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search Center or Class..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: '#94a3b8', mr: 1, fontSize: 18 }} />,
            }}
            sx={{
              bgcolor: 'white',
              minWidth: { xs: '100%', sm: 240 },
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
                fontSize: '14px',
                '& fieldset': { borderColor: '#cbd5e1' },
                '&:hover fieldset': { borderColor: '#94a3b8' },
                '&.Mui-focused fieldset': { borderColor: '#0070d2' }
              }
            }}
          />
          <Tooltip title="Refresh">
            <IconButton
              onClick={() => refetch()}
              sx={{
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                p: 0.75,
                bgcolor: 'white',
                '&:hover': { bgcolor: '#f1f5f9' }
              }}
            >
              <RefreshIcon sx={{ color: '#475569', fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Minimalist Summary Cards */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Card
            elevation={0}
            sx={{
              bgcolor: 'white',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              borderLeft: '4px solid #0070d2',
              position: 'relative'
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', letterSpacing: '0.05em' }}>
                KV CENTERS
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.5 }}>
                {totalCenters}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Card
            elevation={0}
            sx={{
              bgcolor: 'white',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              borderLeft: '4px solid #10b981',
              position: 'relative'
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', letterSpacing: '0.05em' }}>
                TOTAL CLASSES
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.5 }}>
                {totalClasses}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Table of KV Centers */}
      {filteredCentersCount === 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 5, bgcolor: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
          <SchoolIcon sx={{ fontSize: 40, color: '#94a3b8', mb: 1.5 }} />
          <Typography variant="subtitle2" sx={{ color: '#475569', fontWeight: 600 }}>No results found</Typography>
          <Typography variant="caption" sx={{ color: '#64748b', mt: 0.5 }}>Try modifying your search filter keywords.</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '8px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <Table sx={{ minWidth: 650 }} aria-label="KV Centers Table">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '13.5px', py: 1.75 }}>S.No</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '13.5px', py: 1.75 }}>KV Center</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '13.5px', py: 1.75 }}>Associated Classes</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#475569', fontSize: '13.5px', py: 1.75, width: '150px' }}>Total Classes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(filteredGroups).map(([sectionName, classesList], idx) => (
                <TableRow
                  key={sectionName}
                  sx={{
                    transition: 'background-color 0.15s ease',
                    '&:hover': { bgcolor: '#f8fafc' },
                    '&:not(:last-child)': { borderBottom: '1px solid #e2e8f0' }
                  }}
                >
                  <TableCell sx={{ color: '#475569', fontWeight: 500, fontSize: '13.5px', py: 1.5 }}>
                    {idx + 1}
                  </TableCell>
                  <TableCell sx={{ py: 1.5 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <CorporateFareIcon sx={{ fontSize: 18, color: '#0070d2' }} />
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '13.5px' }}>
                        {sectionName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 1.5 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {classesList.map((c) => (
                        <Chip
                          key={c.classId}
                          icon={<SchoolIcon sx={{ fontSize: '14px !important', color: '#64748b !important' }} />}
                          label={c.className}
                          size="small"
                          onClick={() => {
                            const slug = `${c.className} - ${c.original.sectionName || 'General'}`.replace(/\s+/g, '-');
                            navigate(`/classes/${slug}`);
                          }}
                          sx={{
                            bgcolor: '#f1f5f9',
                            color: '#334155',
                            fontWeight: 600,
                            fontSize: '12px',
                            height: '24px',
                            border: '1px solid #e2e8f0',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': { 
                              bgcolor: '#e2e8f0',
                              transform: 'translateY(-1px)',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                            },
                            '& .MuiChip-icon': { mr: -0.5 }
                          }}
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1.5 }}>
                    <Chip
                      label={`${classesList.length}`}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(0, 112, 210, 0.08)',
                        color: '#0070d2',
                        fontWeight: 700,
                        fontSize: '12px',
                        border: '1px solid rgba(0, 112, 210, 0.15)'
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

    </Box>
  );
}
